// =============================================================================
// CRM Lookup Panel — manual CRM search on the /call page
// =============================================================================
// Always-visible search card rendered as a sibling card below the
// call card in CallPage.ts:
//
//     </div>            <!-- end call card -->
//     ${CrmLookupPanel()}
//   </section>
//
// One search input + type chips (Auto / Telefon / Ticket / Maschine / Name)
// with auto-detection of the input type; chips override the detection. Calls
// the BaseQL query `crmLookup` from crm-lookup-resolvers.ts (positional
// curried call, app convention) and renders the JSON result as result cards
// in the app's house style (daisyUI 5 + Tailwind v4, square corners,
// primary #d6001c).
//
// German copy is verbatim per SPEC.md §2 (errors E1–E6, empty states E5–E11,
// caveats C1–C5). Internal test view — displays CRM personal data (SPEC §4).
// =============================================================================

import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { crmLookup } from '../../db/crm-lookup-resolvers.js';

// ─── Types mirror the resolver's JSON payload (over the wire as string) ─────

type CrmInputType = 'phone' | 'case' | 'machine' | 'name';

interface CrmContactUI {
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  telephone1?: string | null;
  businessPhone?: string | null;
  mobilePhone?: string | null;
  accountNumber?: string | null;
  accountName?: string | null;
}

interface CrmAccountUI {
  name?: string | null;
  accountNumber?: string | null;
  city?: string | null;
  addressLine1?: string | null;
  postalCode?: string | null;
}

interface CrmMachineUI {
  machineNumber?: string | null;
  machineName?: string | null;
  productGroup?: string | null;
}

interface CrmCaseUI {
  ticketNumber: string;
  machineNumber?: string | null;
  detail?: {
    title?: string | null;
    statusCode?: string | null;
    priorityCode?: string | null;
  } | null;
}

interface CrmLookupResultUI {
  ok: boolean;
  error?: string;
  inputType?: CrmInputType;
  contact?: CrmContactUI | null;
  serviceEngineer?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    businessUnit?: string | null;
    resourceGroups?: string[];
  } | null;
  contacts?: CrmContactUI[] | null;
  account?: CrmAccountUI | null;
  machines?: { count: number; items: CrmMachineUI[] } | null;
  cases?: CrmCaseUI[] | null;
  caseDetail?: Record<string, unknown> | null;
  notes?: string[] | null;
}

// ─── German copy (verbatim per SPEC.md §2 — reviewers diff these strings) ───

const ERROR_E1 = 'Bridge nicht erreichbar. Bitte SSH-Tunnel/MCP-Server prüfen und erneut versuchen.';
const ERROR_E2 =
  'Ticket nicht gefunden oder Bridge-Fehler. Die Bridge liefert hier keine eindeutige Antwort – Ticketnummer prüfen und ggf. erneut versuchen.';
const ERROR_E4 = 'Zeitüberschreitung bei der CRM-Abfrage (30 s). Bitte erneut versuchen.';
const INLINE_E3 = 'Namenssuche benötigt eine Kundennummer.';
const INLINE_E6 = 'Bitte gültige Telefonnummer eingeben (mind. 7 Ziffern).';
const EMPTY_E5 = 'Kein Kontakt zu dieser Telefonnummer gefunden.';
const EMPTY_E7 = 'Keine Maschine mit dieser Nummer gefunden.';
const EMPTY_E8 = 'Keine offenen Tickets zu dieser Maschine.';
const EMPTY_E11 = 'Noch keine Suche.';
const emptyE9 = (kundennummer: string) => `Kein Kontakt mit diesem Namen im Account ${kundennummer}.`;
const emptyE10 = (kundennummer: string) => `Kunde ${kundennummer} nicht gefunden.`;
const CAVEAT_C1 = 'Nur OFFENE Tickets werden gelistet.';
const CAVEAT_C3 = 'Hinweis: mehrere Kontakte mit derselben Telefonnummer.';
const BADGE_C5 = 'Interne Testansicht – enthält personenbezogene CRM-Daten';

// ─── Input type auto-detection (per SPEC detection rules) ────────────────────
// starts with '+' OR ≥8 pure digits → phone
// /^cas\d+$/i                       → case (Ticket)
// 6–7 pure digits                   → case (best guess — ambiguous vs.
//                                     Maschinennummer, chips override)
// alphanumeric with letters+digits  → machine (Dossier, e.g. F41453)
// letters/spaces only               → name

export const detectCrmInputType = (raw: string): CrmInputType | null => {
  const v = raw.trim();
  if (!v) return null;
  if (v.startsWith('+')) return 'phone';
  if (/^cas\d+$/i.test(v)) return 'case';
  if (/^[\d\s\-/().]+$/.test(v)) {
    const digits = v.replace(/\D/g, '');
    if (digits.length >= 8) return 'phone';
    // 7 digits starting '1' = CAS body (1151008, 1096294, …); other 4-7 digits = machine/dossier
    if (digits.length === 7 && digits.startsWith('1')) return 'case';
    if (digits.length >= 4 && digits.length <= 7) return 'machine'; // chip can force 'case'
    if (digits.length > 0) return 'machine';
    return null;
  }
  // alphanumeric with digits, e.g. F41453, 725069-A → machine / dossier
  if (/^[A-Za-z0-9\-/_.]+$/.test(v) && /\d/.test(v)) return 'machine';
  // plain letters (and spaces, umlauts) → name
  return 'name';
};

const TYPE_CHIPS: { type: CrmInputType; label: string }[] = [
  { type: 'phone', label: 'Telefon' },
  { type: 'case', label: 'Ticket' },
  { type: 'machine', label: 'Maschine' },
  { type: 'name', label: 'Name' },
];

const typeLabel = (t: CrmInputType | null): string => TYPE_CHIPS.find(c => c.type === t)?.label ?? '—';

// ─── Error classification (SPEC §1.2 C8: E1 vs E2 vs E4 vs empty states) ────
// connection-class rejection → E1 · 30s wrapper abort → E4 · case lookups
// (nonexistent CAS, corrupt record, bridge error — indistinguishable) → E2 ·
// known not-found per type → muted empty state (NEVER alert-error).

const classifyLookupError = (
  message: string,
  searchType: CrmInputType,
  kundennummer: string,
): { kind: 'alert' | 'empty'; text: string } => {
  const m = message.toLowerCase();
  if (m.includes('timed out') || m.includes('timeout') || m.includes('zeitüberschreitung')) {
    return { kind: 'alert', text: ERROR_E4 };
  }
  if (
    m.includes('fetch') ||
    m.includes('econnrefused') ||
    m.includes('econnreset') ||
    m.includes('socket') ||
    m.includes('network') ||
    m.includes('tunnel') ||
    m.includes('nicht erreichbar')
  ) {
    return { kind: 'alert', text: ERROR_E1 };
  }
  if (searchType === 'case') return { kind: 'alert', text: ERROR_E2 };
  const notFound =
    m.includes('nicht gefunden') ||
    m.includes('kein kontakt') ||
    m.includes('keine maschine') ||
    m.includes('not found');
  if (notFound) {
    if (searchType === 'phone') return { kind: 'empty', text: EMPTY_E5 };
    if (searchType === 'machine') return { kind: 'empty', text: EMPTY_E7 };
    if (searchType === 'name') {
      if (m.includes('kontakt')) return { kind: 'empty', text: emptyE9(kundennummer) };
      if (m.includes('kunde') || m.includes('account')) return { kind: 'empty', text: emptyE10(kundennummer) };
      return { kind: 'empty', text: emptyE9(kundennummer) };
    }
  }
  return { kind: 'alert', text: message };
};

// ─── Status badge (E4 §3.5 — confirmed Dynamics statusCode values) ──────────

const caseStatusBadge = (status: string | null | undefined) => {
  if (!status) return html`<span class="badge badge-ghost badge-sm">—</span>`;
  const cls =
    status === 'NewCase'
      ? 'badge-info'
      : status === 'InProgress'
        ? 'badge-warning'
        : status === 'ProblemSolved'
          ? 'badge-success'
          : status === 'ClosureRequested'
            ? 'badge-warning'
            : status === 'Cancelled'
              ? 'badge-error'
              : 'badge-ghost';
  return html`<span class="badge ${cls} badge-sm">${status}</span>`;
};

// German labels for known caseDetail keys (unknown keys fall back to raw key)
const CASE_DETAIL_LABELS: Record<string, string> = {
  ticketNumber: 'Ticketnummer',
  title: 'Titel',
  statusCode: 'Status',
  priorityCode: 'Priorität',
  machineNumber: 'Maschine',
  description: 'Beschreibung',
  createdOn: 'Erstellt',
  customerName: 'Kunde',
  accountNumber: 'Kundennummer',
  contactName: 'Kontakt',
};

// =============================================================================
// Component
// =============================================================================

export const CrmLookupPanel = stateful(() => {
  // ── state ──────────────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [accountNumber, setAccountNumber] = useState(''); // required for name search
  const [forcedType, setForcedType] = useState<CrmInputType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null); // alert-error class (E1/E2/E4/raw)
  const [notFound, setNotFound] = useState<string | null>(null); // muted empty state (E5/E7/E9/E10)
  const [data, setData] = useState<CrmLookupResultUI | null>(null);
  const [searched, setSearched] = useState(false);
  const [searchedType, setSearchedType] = useState<CrmInputType | null>(null);
  const [searchedAccount, setSearchedAccount] = useState('');
  const [machineFilter, setMachineFilter] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const detectedType = detectCrmInputType(query);
  const effectiveType: CrmInputType | null = forcedType ?? detectedType;

  // client-side phone validation (SPEC P6 / E6) — no bridge call for short numbers
  const phoneDigitCount = query.replace(/\D/g, '').length;
  const phoneTooShort = effectiveType === 'phone' && query.trim().length > 0 && phoneDigitCount < 7;
  // client-side name guard (SPEC N1 / E3) — no bridge call without Kundennummer
  const nameMissingAccount = effectiveType === 'name' && query.trim().length > 0 && !accountNumber.trim();

  // ── helpers ────────────────────────────────────────────────────────────────

  const copyValue = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      // clipboard unavailable (http / permissions) — ignore silently
    }
  };

  /** tiny copy-to-clipboard button for CAS numbers / phone numbers */
  const copyButton = (key: string, value: string | null | undefined) =>
    value
      ? html`
          <button
            class="btn btn-xs btn-outline px-1 ml-1 align-middle"
            title="In Zwischenablage kopieren"
            @click=${() => void copyValue(key, value)}
          >
            ${copiedKey === key
              ? '✓ Kopiert!'
              : html`
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V5a2 2 0 012-2h9a2 2 0 012 2v9a2 2 0 01-2 2h-2M5 9h9a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2v-9a2 2 0 012-2z"
                    />
                  </svg>
                `}
          </button>
        `
      : nothing;

  const canSubmit = (): boolean => {
    if (loading) return false;
    if (!query.trim() || !effectiveType) return false;
    if (effectiveType === 'name' && !accountNumber.trim()) return false;
    if (phoneTooShort) return false;
    return true;
  };

  const submit = async () => {
    const value = query.trim();
    const type = effectiveType;
    if (!value || !type || loading) return;
    if (type === 'name' && !accountNumber.trim()) return; // inline E3 already shown
    if (type === 'phone' && phoneDigitCount < 7) return; // inline E6 already shown
    setLoading(true);
    setError(null);
    setNotFound(null);
    setData(null);
    setMachineFilter('');
    setSearched(true);
    setSearchedType(type);
    setSearchedAccount(type === 'name' ? accountNumber.trim() : '');
    try {
      // positional curried call — app convention (cf. testLabSaveFeedback(a, b, c)());
      // '' for the absent optional accountNumber per resolver param convention.
      const runLookup = async (t: CrmInputType): Promise<CrmLookupResultUI> => {
        const raw = (await crmLookup(t, value, t === 'name' ? accountNumber.trim() : '')()) as unknown as string;
        return JSON.parse(raw) as CrmLookupResultUI;
      };
      let usedType: CrmInputType = type;
      let parsed = await runLookup(type);
      // Auto mode: 4-7 pure digits are ambiguous (machine OR ticket). If the first guess
      // comes back empty, silently try the other type once and prefer whichever has data.
      const digitsOnly = value.replace(/\D/g, '');
      const ambiguous = !forcedType && /^[0-9]+$/.test(value) && digitsOnly.length >= 4 && digitsOnly.length <= 7;
      if (ambiguous && (type === 'case' || type === 'machine') && (!parsed.ok || !hasAnyData(parsed))) {
        const other: CrmInputType = type === 'case' ? 'machine' : 'case';
        try {
          const second = await runLookup(other);
          if (second.ok && hasAnyData(second)) {
            second.notes = [
              other === 'machine'
                ? 'Automatisch als Maschinen-/Dossiernummer interpretiert (als Ticket nicht gefunden)'
                : 'Automatisch als Ticketnummer interpretiert (als Maschine nicht gefunden)',
              ...(second.notes ?? []),
            ];
            parsed = second;
            usedType = other;
          }
        } catch {
          // fallback attempt failed — keep the first result
        }
      }
      setSearchedType(usedType);
      if (!parsed.ok) {
        const classified = classifyLookupError(
          parsed.error ?? 'Unbekannter Fehler bei der CRM-Suche.',
          usedType,
          accountNumber.trim(),
        );
        if (classified.kind === 'empty') setNotFound(classified.text);
        else setError(classified.text);
      } else {
        setData(parsed);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const classified = classifyLookupError(message, type, accountNumber.trim());
      if (classified.kind === 'empty') setNotFound(classified.text);
      else setError(classified.text);
    } finally {
      setLoading(false);
    }
  };

  const hasAnyData = (d: CrmLookupResultUI): boolean =>
    !!(
      d.contact ||
      d.serviceEngineer ||
      d.account ||
      (d.contacts && d.contacts.length > 0) ||
      (d.machines && (d.machines.count > 0 || d.machines.items.length > 0)) ||
      (d.cases && d.cases.length > 0) ||
      (d.caseDetail && Object.keys(d.caseDetail).length > 0)
    );

  /** per-type empty-state copy when the lookup succeeded but returned nothing */
  const emptyStateFor = (t: CrmInputType | null): string => {
    if (t === 'phone') return EMPTY_E5;
    if (t === 'machine') return EMPTY_E7;
    if (t === 'name') return data?.account ? emptyE9(searchedAccount) : emptyE10(searchedAccount);
    return 'Keine Daten gefunden.';
  };

  // ── render helpers: result sections ───────────────────────────────────────

  const renderContactCard = (contact: CrmContactUI) => {
    const name = contact.fullName ?? ([contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—');
    const phone = contact.businessPhone ?? contact.telephone1 ?? null;
    return html`
      <div class="card bg-base-200 border border-primary/30 mb-4">
        <div class="card-body p-4">
          <h3 class="font-semibold mb-2 flex items-center gap-2">
            ${name}
            <span class="badge badge-accent badge-sm font-mono">Kontakt</span>
          </h3>
          <div class="grid grid-cols-2 gap-x-4 gap-y-1">
            <span class="text-base-content/70">E-Mail:</span>
            <span>${contact.email ?? '—'}</span>
            <span class="text-base-content/70">Telefon:</span>
            <span>${phone ?? '—'}${copyButton('contact-phone', phone)}</span>
            <span class="text-base-content/70">Mobil:</span>
            <span>${contact.mobilePhone ?? '—'}${copyButton('contact-mobilePhone', contact.mobilePhone)}</span>
            <span class="text-base-content/70">Firma:</span>
            <span>
              ${contact.accountName ?? '—'}
              ${contact.accountNumber
                ? html`<span class="font-mono text-xs text-base-content/60">(${contact.accountNumber})</span>`
                : nothing}
            </span>
          </div>
        </div>
      </div>
    `;
  };

  const renderEngineerCard = (engineer: NonNullable<CrmLookupResultUI['serviceEngineer']>) =>
    html` <div class="card bg-base-200 border border-primary/30 mb-4">
      <div class="card-body p-4">
        <h3 class="font-semibold mb-2 flex flex-wrap items-center gap-2">
          ${[engineer.firstName, engineer.lastName].filter(Boolean).join(' ') || '—'}
          <span class="badge badge-primary badge-sm">Service Engineer</span>
        </h3>
        <dl class="grid grid-cols-2 gap-x-4 gap-y-1">
          <dt>E-Mail:</dt>
          <dd class="break-all">${engineer.email || '—'}</dd>
          <dt>Geschäftsbereich:</dt>
          <dd>${engineer.businessUnit || '—'}</dd>
          <dt>Ressourcengruppen:</dt>
          <dd>${engineer.resourceGroups?.join(', ') || '—'}</dd>
        </dl>
      </div>
    </div>`;

  const renderAccountCard = (account: CrmAccountUI) => html`
    <div class="card bg-base-200 border border-primary/30 mb-4">
      <div class="card-body p-4">
        <h3 class="font-semibold mb-2 flex items-center gap-2">
          ${account.name ?? '—'}
          <span class="badge badge-accent badge-sm font-mono">Account</span>
        </h3>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1">
          <span class="text-base-content/70">Kundennummer:</span>
          <span class="font-mono">${account.accountNumber ?? '—'}</span>
          <span class="text-base-content/70">Adresse:</span>
          <span>${account.addressLine1 ?? '—'}</span>
          <span class="text-base-content/70">Ort:</span>
          <span>${[account.postalCode, account.city].filter(Boolean).join(' ') || '—'}</span>
        </div>
      </div>
    </div>
  `;

  const renderAccountContactsCard = (contacts: CrmContactUI[]) => html`
    <div class="card bg-base-200 border border-primary/30 mb-4">
      <div class="card-body p-4">
        <h3 class="font-semibold mb-2 flex items-center gap-2">
          Kontakte des Kontos
          <span class="badge badge-ghost badge-sm">${contacts.length}</span>
        </h3>
        <div class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Telefon</th>
              </tr>
            </thead>
            <tbody>
              ${contacts.map(
                (c, i) => html`
                  <tr>
                    <td>${c.fullName ?? ([c.firstName, c.lastName].filter(Boolean).join(' ') || '—')}</td>
                    <td>${c.email ?? '—'}</td>
                    <td class="font-mono text-xs">
                      ${c.businessPhone ?? c.telephone1 ?? c.mobilePhone ?? '—'}${copyButton(
                        `acc-contact-phone-${i}`,
                        c.businessPhone ?? c.telephone1 ?? c.mobilePhone,
                      )}
                    </td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
        </div>
        <p class="text-xs text-base-content/60 mt-1">
          Anzeige ggf. begrenzt — nicht alle Kontakte des Kontos werden aufgeführt.
        </p>
      </div>
    </div>
  `;

  const renderMachinesCard = (machines: { count: number; items: CrmMachineUI[] }) => {
    const filter = machineFilter.trim().toLowerCase();
    const filtered = filter
      ? machines.items.filter(m =>
          [m.machineNumber, m.machineName, m.productGroup]
            .filter(Boolean)
            .some(v => String(v).toLowerCase().includes(filter)),
        )
      : machines.items;
    const shown = filtered.slice(0, 50);
    // server already slices to 50 + real total count — C2 caveat when truncated
    const truncated = machines.count > machines.items.length && machines.items.length > 0;
    return html`
      <div class="card bg-base-200 border border-primary/30 mb-4">
        <div class="card-body p-4">
          <h3 class="font-semibold mb-2 flex items-center gap-2">
            ${truncated ? `Maschinen (${machines.items.length} von ${machines.count} angezeigt)` : 'Maschinen'}
            <span class="badge badge-ghost badge-sm">${machines.count}</span>
          </h3>
          ${machines.items.length > 10
            ? html`
                <input
                  type="text"
                  class="input input-bordered input-sm w-full mb-2"
                  placeholder="Maschinen filtern (Nummer, Name, Produktgruppe)…"
                  .value=${machineFilter}
                  @input=${(e: Event) => setMachineFilter((e.target as HTMLInputElement).value)}
                />
              `
            : nothing}
          ${shown.length === 0
            ? html`<p class="text-base-content/50 italic">
                Keine Maschinen${filter ? ' für diesen Filter' : ''} gefunden.
              </p>`
            : html`
                <div class="overflow-x-auto">
                  <table class="table table-sm">
                    <thead>
                      <tr>
                        <th>Maschinennr.</th>
                        <th>Bezeichnung</th>
                        <th>Produktgruppe</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${shown.map(
                        m => html`
                          <tr>
                            <td class="font-mono text-xs">${m.machineNumber ?? '—'}</td>
                            <td>${m.machineName ?? '—'}</td>
                            <td>${m.productGroup ?? '—'}</td>
                          </tr>
                        `,
                      )}
                    </tbody>
                  </table>
                </div>
              `}
          ${truncated
            ? html`<p class="text-sm text-base-content/70 mt-1">
                Maschinenliste gekürzt auf ${machines.items.length} von ${machines.count}. Vollständige Suche über die
                Maschinennummer.
              </p>`
            : nothing}
        </div>
      </div>
    `;
  };

  const renderCasesCard = (cases: CrmCaseUI[]) => html`
    <div class="card bg-base-200 border border-primary/30 mb-4">
      <div class="card-body p-4">
        <h3 class="font-semibold mb-2 flex items-center gap-2">
          Offene Tickets
          <span class="badge badge-ghost badge-sm">${cases.length}</span>
        </h3>
        <div class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>CAS-Nr.</th>
                <th>Maschine</th>
                <th>Titel</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${cases.map(
                (c, i) => html`
                  <tr>
                    <td class="font-mono text-xs whitespace-nowrap">
                      ${c.ticketNumber}${copyButton(`case-${i}`, c.ticketNumber)}
                    </td>
                    <td class="font-mono text-xs">${c.machineNumber ?? '—'}</td>
                    <td>${c.detail?.title ?? '—'}</td>
                    <td>${caseStatusBadge(c.detail?.statusCode)}</td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
        </div>
        <p class="text-sm text-base-content/70 mt-1">${CAVEAT_C1}</p>
      </div>
    </div>
  `;

  /** SPEC M5 / E8 — machine search found data but zero open tickets */
  const renderNoOpenTicketsCard = () => html`
    <div class="card bg-base-200 border border-primary/30 mb-4">
      <div class="card-body p-4">
        <h3 class="font-semibold mb-2">Offene Tickets</h3>
        <p class="text-base-content/50 italic">${EMPTY_E8}</p>
        <p class="text-sm text-base-content/70 mt-1">${CAVEAT_C1}</p>
      </div>
    </div>
  `;

  const renderCaseDetailCard = (detail: Record<string, unknown>) => {
    const entries = Object.entries(detail).filter(
      ([, v]) =>
        v !== null && v !== undefined && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'),
    );
    return html`
      <div class="card bg-base-200 border border-primary/30 mb-4">
        <div class="card-body p-4">
          <h3 class="font-semibold mb-2 flex items-center gap-2">
            Ticket-Details
            ${typeof detail['ticketNumber'] === 'string'
              ? html`
                  <span class="badge badge-accent badge-sm font-mono">${detail['ticketNumber']}</span>
                  ${copyButton('caseDetail-ticketNumber', detail['ticketNumber'] as string)}
                `
              : nothing}
          </h3>
          ${entries.length === 0
            ? html`<p class="text-base-content/50 italic">Keine Details verfügbar.</p>`
            : html`
                <div class="grid grid-cols-2 gap-x-4 gap-y-1">
                  ${entries.map(([key, value]) =>
                    key === 'statusCode'
                      ? html`
                          <span class="text-base-content/70">${CASE_DETAIL_LABELS[key] ?? key}:</span>
                          <span>${caseStatusBadge(String(value))}</span>
                        `
                      : html`
                          <span class="text-base-content/70">${CASE_DETAIL_LABELS[key] ?? key}:</span>
                          <span>${String(value)}</span>
                        `,
                  )}
                </div>
              `}
          <p class="text-sm text-base-content/70 mt-2">${CAVEAT_C1}</p>
        </div>
      </div>
    `;
  };

  // server-emitted caveats (C2/C4 etc.) — SPEC §2.3: caveats use alert-info
  const renderNotes = (notes: string[]) => html`
    <div class="alert alert-info mb-4">
      <div class="text-sm">${notes.map(n => html`<div>• ${n}</div>`)}</div>
    </div>
  `;

  // ── render: results block ──────────────────────────────────────────────────

  /** Serialize the whole result as clean plain text (for pasting into chat / email). */
  const formatResultText = (d: CrmLookupResultUI): string => {
    const L: string[] = [];
    const dash = (v: unknown) => (v === undefined || v === null || v === '' ? '—' : String(v));
    const phoneOf = (c: Record<string, any>) => c.telephone1 || c.businessPhone || c.mobilePhone || '';
    L.push(`CRM-Suche — ${dash(searchedType)}: ${query.trim()} (${new Date().toLocaleString('de-DE')})`);
    L.push('═'.repeat(60));
    if (d.serviceEngineer) {
      const e = d.serviceEngineer;
      L.push(
        '',
        'SERVICE ENGINEER',
        `  Name: ${[e.firstName, e.lastName].filter(Boolean).join(' ') || '—'}`,
        `  E-Mail: ${dash(e.email)}`,
        `  Geschäftsbereich: ${dash(e.businessUnit)}`,
      );
    }
    if (d.contact) {
      const c = d.contact;
      L.push('', 'KONTAKT');
      L.push(`  Name:    ${dash(((c.firstName || '') + ' ' + (c.lastName || '')).trim())}`);
      L.push(`  E-Mail:  ${dash(c.email)}`);
      L.push(`  Telefon: ${dash(c.businessPhone || c.telephone1)}`);
      if (c.mobilePhone) L.push(`  Mobil:   ${c.mobilePhone}`);
      L.push(`  Firma:   ${dash(c.accountName)}${c.accountNumber ? ` (${c.accountNumber})` : ''}`);
    }
    if (d.account) {
      const a = d.account;
      L.push('', 'ACCOUNT');
      L.push(`  Kundennummer: ${dash(a.accountNumber)}`);
      L.push(`  Name:    ${dash(a.name)}`);
      L.push(`  Adresse: ${dash(a.addressLine1)}`);
      L.push(`  Ort:     ${[a.postalCode, a.city].filter(Boolean).join(' ') || '—'}`);
    }
    if (d.contacts && d.contacts.length > 0) {
      L.push('', `KONTAKTE DES KONTOS (${d.contacts.length})`);
      for (const c of d.contacts) {
        L.push(
          `  - ${((c.firstName || '') + ' ' + (c.lastName || '')).trim() || '—'} | ${c.email || '—'} | ${phoneOf(c) || '—'}`,
        );
      }
    }
    if (d.machines && (d.machines.count > 0 || d.machines.items.length > 0)) {
      const shown = d.machines.items.length;
      L.push('', `MASCHINEN (${shown}${d.machines.count > shown ? ` von ${d.machines.count}` : ''})`);
      for (const m of d.machines.items) {
        L.push(`  - ${m.machineNumber || '—'} | ${m.machineName || '—'} | ${m.productGroup || '—'}`);
      }
    }
    if (d.cases && d.cases.length > 0) {
      L.push('', `OFFENE TICKETS (${d.cases.length})`);
      for (const cs of d.cases) {
        L.push(
          `  - ${cs.ticketNumber} | Maschine: ${cs.machineNumber || '—'} | ${cs.detail?.title || '—'} | ${cs.detail?.statusCode || '—'}`,
        );
      }
    }
    if (d.caseDetail && Object.keys(d.caseDetail).length > 0) {
      const t = d.caseDetail;
      L.push('', 'TICKET-DETAILS');
      L.push(`  Ticketnummer: ${dash(t.ticketNumber)}`);
      L.push(`  Titel:        ${dash(t.title)}`);
      L.push(`  Status:       ${dash(t.statusCode)}`);
      L.push(`  Priorität:    ${dash(t.priorityCode)}`);
      L.push(`  Kundennummer: ${dash(t.accountNumber)}${t.accountName ? ` (${t.accountName})` : ''}`);
      L.push(`  Maschine:     ${dash(t.machineNumber)}`);
    }
    if (d.notes && d.notes.length > 0) {
      L.push('', 'HINWEISE');
      for (const n of d.notes) L.push(`  • ${n}`);
    }
    return L.join('\n');
  };

  const renderResults = () => {
    if (loading) {
      return html`
        <div class="flex flex-col items-center justify-center min-h-[120px]">
          <span class="loading loading-spinner loading-lg text-primary mb-4"></span>
          <span class="text-base-content/70">Lade CRM-Daten …</span>
        </div>
      `;
    }
    if (error) {
      return html`<div class="alert alert-error mb-4"><span>${error}</span></div>`;
    }
    if (notFound) {
      return html`<p class="text-base-content/50 italic">${notFound}</p>`;
    }
    if (!data) {
      return searched ? nothing : html`<p class="text-base-content/50 italic">${EMPTY_E11}</p>`;
    }
    if (!hasAnyData(data)) {
      if (searchedType === 'case') {
        // nonexistent CAS is indistinguishable from a bridge failure → E2 alert
        return html`
          ${data.notes && data.notes.length > 0 ? renderNotes(data.notes) : nothing}
          <div class="alert alert-error mb-4"><span>${ERROR_E2}</span></div>
        `;
      }
      return html`
        ${data.notes && data.notes.length > 0 ? renderNotes(data.notes) : nothing}
        <p class="text-base-content/50 italic">${emptyStateFor(searchedType)}</p>
      `;
    }
    return html`
      <div class="flex justify-end mb-2">
        <button
          class="btn btn-sm btn-outline"
          title="Gesamtes Ergebnis als Text in die Zwischenablage kopieren"
          @click=${() => void copyValue('__all__', formatResultText(data))}
        >
          ${copiedKey === '__all__' ? '✓ Kopiert!' : 'Alles kopieren'}
        </button>
      </div>
      ${data.notes && data.notes.length > 0 ? renderNotes(data.notes) : nothing}
      ${searchedType === 'phone' && data.contacts && data.contacts.length > 1
        ? html`<div class="alert alert-info mb-4"><span>${CAVEAT_C3}</span></div>`
        : nothing}
      ${data.contact ? renderContactCard(data.contact) : nothing}
      ${data.serviceEngineer ? renderEngineerCard(data.serviceEngineer) : nothing}
      ${data.account ? renderAccountCard(data.account) : nothing}
      ${data.contacts && data.contacts.length > 0 ? renderAccountContactsCard(data.contacts) : nothing}
      ${data.machines && (data.machines.count > 0 || data.machines.items.length > 0)
        ? renderMachinesCard(data.machines)
        : nothing}
      ${data.cases && data.cases.length > 0
        ? renderCasesCard(data.cases)
        : searchedType === 'machine'
          ? renderNoOpenTicketsCard()
          : nothing}
      ${data.caseDetail && Object.keys(data.caseDetail).length > 0 ? renderCaseDetailCard(data.caseDetail) : nothing}
    `;
  };

  // Search remains visible across re-renders; no collapse control or automatic CRM lookup.
  const renderForm = () => html`
    <div class="zw-crm-search-row">
      <label class="zw-crm-search-field">
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="10.5" cy="10.5" r="6.5" stroke-width="1.8" />
          <path d="m16 16 4 4" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        <input
          type="search"
          aria-label="CRM durchsuchen"
          placeholder="Telefonnummer, CAS, Dossiernummer oder Name"
          .value=${query}
          @input=${(e: Event) => setQuery((e.target as HTMLInputElement).value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter') void submit();
          }}
        />
      </label>
      <button type="button" class="zw-crm-search-button" ?disabled=${!canSubmit()} @click=${() => void submit()}>
        ${loading ? html`<span class="loading loading-spinner loading-xs"></span>Suche…` : 'Suchen'}
      </button>
    </div>
    ${phoneTooShort ? html`<p class="zw-crm-error" role="alert">${INLINE_E6}</p>` : nothing}
    <div class="zw-crm-filter-row">
      <div class="zw-crm-filters" role="group" aria-label="Suchtyp">
        <button
          type="button"
          class="zw-crm-filter ${forcedType === null ? 'is-active' : ''}"
          aria-pressed=${forcedType === null ? 'true' : 'false'}
          title="Automatische Erkennung"
          @click=${() => setForcedType(null)}
        >
          Automatisch
        </button>
        ${TYPE_CHIPS.map(
          chip =>
            html` <button
              type="button"
              class="zw-crm-filter ${forcedType === chip.type ? 'is-active' : ''}"
              aria-pressed=${forcedType === chip.type ? 'true' : 'false'}
              title=${forcedType === chip.type ? 'Manuell gewählt' : 'Typ erzwingen'}
              @click=${() => setForcedType(forcedType === chip.type ? null : chip.type)}
            >
              ${chip.label}
            </button>`,
        )}
      </div>
      ${forcedType === null && detectedType
        ? html`<span class="zw-crm-detected">Erkannt: <strong>${typeLabel(detectedType)}</strong></span>`
        : nothing}
    </div>
    ${effectiveType === 'name'
      ? html`
          <label class="zw-crm-account-field">
            <span>Kundennummer</span>
            <input
              type="text"
              placeholder="z. B. 102798"
              .value=${accountNumber}
              @input=${(e: Event) => setAccountNumber((e.target as HTMLInputElement).value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') void submit();
              }}
            />
          </label>
          ${nameMissingAccount ? html`<p class="zw-crm-error" role="alert">${INLINE_E3}</p>` : nothing}
        `
      : nothing}
  `;

  return html`
    <style>
      .zw-crm-panel {
        margin-top: 24px;
        padding: 24px;
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        color: #1d2939;
        box-shadow: 0 2px 8px rgb(16 24 40 / 3%);
        min-width: 0;
      }
      .zw-crm-panel .zw-crm-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 16px;
        margin-bottom: 22px;
      }
      .zw-crm-panel .zw-crm-heading {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .zw-crm-panel .zw-crm-icon {
        display: grid;
        place-items: center;
        width: 42px;
        height: 42px;
        flex: 0 0 42px;
        background: #fff1f3;
        color: #d6001c;
        border-radius: 10px;
      }
      .zw-crm-panel .zw-crm-icon svg {
        width: 22px;
        height: 22px;
      }
      .zw-crm-panel h2 {
        margin: 0;
        font-size: 21px;
        line-height: 1.35;
        font-weight: 700;
        color: #1d2939;
      }
      .zw-crm-panel .zw-crm-subtitle {
        margin: 4px 0 0;
        font-size: 14px;
        line-height: 1.6;
        color: #667085;
      }
      .zw-crm-panel .zw-crm-privacy {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 6px 10px;
        border: 1px solid #f1dfb7;
        border-radius: 7px;
        background: #fffaeb;
        color: #80551b;
        font-size: 12px;
        line-height: 1.5;
      }
      .zw-crm-panel .zw-crm-search-row {
        display: flex;
        align-items: stretch;
        gap: 10px;
      }
      .zw-crm-panel .zw-crm-search-field {
        display: flex;
        flex: 1 1 0;
        align-items: center;
        min-width: 0;
        gap: 10px;
        padding: 0 14px;
        border: 1px solid #cfd5df;
        border-radius: 8px;
        background: #fff;
        transition:
          border-color 0.15s,
          box-shadow 0.15s;
      }
      .zw-crm-panel .zw-crm-search-field:focus-within {
        border-color: #d6001c;
        box-shadow: 0 0 0 3px rgb(214 0 28 / 9%);
      }
      .zw-crm-panel .zw-crm-search-field svg {
        width: 20px;
        height: 20px;
        flex: 0 0 20px;
        color: #667085;
      }
      .zw-crm-panel .zw-crm-search-field input {
        width: 100%;
        min-width: 0;
        height: 46px;
        padding: 0;
        border: 0;
        outline: none;
        background: transparent;
        font: inherit;
        font-size: 14px;
        color: #1d2939;
      }
      .zw-crm-panel input::placeholder {
        color: #788294;
        opacity: 1;
      }
      .zw-crm-panel .zw-crm-search-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 46px;
        padding: 0 24px;
        border: 1px solid #d6001c;
        border-radius: 8px;
        background: #d6001c;
        color: white;
        font: inherit;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
      }
      .zw-crm-panel .zw-crm-search-button:hover:not(:disabled) {
        background: #b80018;
      }
      .zw-crm-panel .zw-crm-search-button:disabled {
        background: #f2f4f7;
        border-color: #e4e7ec;
        color: #98a2b3;
        cursor: not-allowed;
      }
      .zw-crm-panel button:focus-visible {
        outline: 2px solid #d6001c;
        outline-offset: 3px;
      }
      .zw-crm-panel .zw-crm-filter-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
        margin: 14px 0 0;
      }
      .zw-crm-panel .zw-crm-filters {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        padding: 4px;
        border: 1px solid #eaecf0;
        border-radius: 9px;
        background: #f8f9fb;
      }
      .zw-crm-panel .zw-crm-filter {
        padding: 7px 12px;
        border: 1px solid transparent;
        border-radius: 6px;
        background: transparent;
        color: #667085;
        font: inherit;
        font-size: 13px;
        line-height: 1.4;
        cursor: pointer;
      }
      .zw-crm-panel .zw-crm-filter:hover {
        color: #1d2939;
        background: #fff;
      }
      .zw-crm-panel .zw-crm-filter.is-active {
        border-color: #e4e7ec;
        background: #fff;
        color: #b80018;
        font-weight: 600;
        box-shadow: 0 1px 3px rgb(16 24 40 / 5%);
      }
      .zw-crm-panel .zw-crm-detected {
        font-size: 12px;
        color: #667085;
      }
      .zw-crm-panel .zw-crm-account-field {
        display: grid;
        gap: 7px;
        margin-top: 16px;
        width: 100%;
        max-width: 320px;
        font-size: 13px;
        font-weight: 600;
      }
      .zw-crm-panel .zw-crm-account-field input {
        box-sizing: border-box;
        width: 100%;
        padding: 11px 12px;
        border: 1px solid #cfd5df;
        border-radius: 8px;
        font: inherit;
        font-weight: 400;
        background: #fff;
        color: #1d2939;
      }
      .zw-crm-panel .zw-crm-account-field input:focus-visible {
        outline: 2px solid #d6001c;
        outline-offset: 2px;
      }
      .zw-crm-panel .zw-crm-error {
        margin: 8px 0 0;
        color: #b42318;
        font-size: 13px;
      }
      .zw-crm-panel .zw-crm-results {
        margin-top: 20px;
        padding-top: 18px;
        border-top: 1px solid #eaecf0;
        min-width: 0;
      }
      @media (max-width: 600px) {
        .zw-crm-panel {
          padding: 18px;
        }
        .zw-crm-panel .zw-crm-header {
          gap: 12px;
          margin-bottom: 18px;
        }
        .zw-crm-panel .zw-crm-search-row {
          flex-wrap: wrap;
        }
        .zw-crm-panel .zw-crm-search-field {
          flex-basis: 100%;
        }
        .zw-crm-panel .zw-crm-search-button {
          width: 100%;
        }
        .zw-crm-panel .zw-crm-filter {
          padding: 7px 9px;
        }
      }
    </style>
    <section class="zw-crm-panel" aria-label="CRM-Suche">
      <div class="zw-crm-header">
        <div class="zw-crm-heading">
          <span class="zw-crm-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
              <ellipse cx="12" cy="5" rx="8" ry="3" />
              <path d="M4 5v7c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12v7c0 1.7 3.6 3 8 3s8-1.3 8-3v-7" />
            </svg>
          </span>
          <div>
            <h2>CRM-Suche</h2>
            <p class="zw-crm-subtitle">Kontakte, Maschinen und Tickets schnell finden.</p>
          </div>
        </div>
        <span class="zw-crm-privacy">${BADGE_C5}</span>
      </div>
      ${renderForm()}
      <div class="zw-crm-results" aria-live="polite" aria-busy=${loading ? 'true' : 'false'}>${renderResults()}</div>
    </section>
  `;
});
